/**
 * Utility class for sorting accessory carousel items (e.g. Wireless Accessories, Sport Lock).
 *
 * Sorting behaviour:
 * - When a PIM or Sitecore sort order is provided via the `data-filter-sortorder` attribute,
 *   items are sorted by that numeric value (ascending or descending).
 * - Items that have a sort order are always ranked before items that do not.
 * - When no sort order is specified on any item, items fall back to alphabetical order
 *   based on their title or SKU.
 * - Items that share the same sort order value are further sorted alphabetically, then
 *   by their original DOM position to guarantee a stable result.
 */
class Sorting {
  /**
   * Reads and normalises the `data-filter-sortorder` attribute of a DOM element.
   *
   * @param {Element|null} item - The carousel item element to inspect.
   * @returns {number|null} The sort order as a finite number, or `null` when the
   *   attribute is absent, empty, or non-numeric.
   */
  static _getNormalizedSortOrder(item) {
    const rawSortOrder = item ? item.getAttribute('data-filter-sortorder') : null;
    if (rawSortOrder == null || rawSortOrder === '') return null;

    const numericSortOrder = Number(rawSortOrder);
    return Number.isFinite(numericSortOrder) ? numericSortOrder : null;
  }

  /**
   * Derives a normalised string used for alphabetical tie-breaking.
   * Prefers the item's visible title; falls back to SKU, then an empty string.
   *
   * @param {Element} item - The carousel item element to inspect.
   * @returns {string} Trimmed, lower-cased display name for locale-aware comparison.
   */
  static _getAlphabeticalName(item) {
    const title = item.querySelector('.configurator__accessory-title')?.innerText;
    const sku = item.querySelector('.configurator__accessory-sku')?.innerText;
    return (title || sku || '').trim().toLowerCase();
  }

  /**
   * Sorts an array of carousel item elements according to their sort order and name.
   *
   * Sort priority (highest to lowest):
   * 1. Numeric sort order from `data-filter-sortorder` (direction controlled by `sortDirection`).
   * 2. Items with a sort order always precede items without one.
   * 3. Alphabetical order by title / SKU when sort orders are equal or absent.
   * 4. Original DOM index to ensure a stable, deterministic result.
   *
   * When *no* item carries a sort order the entire list is sorted purely alphabetically.
   *
   * @param {Element[]} contentItems - Flat array of carousel item DOM elements to sort.
   * @param {object}   [options]
   * @param {'asc'|'desc'} [options.sortDirection='desc'] - Direction for numeric sort order
   *   comparison. Use `'asc'` for ascending (lowest value first) or `'desc'` for descending
   *   (highest value first).
   * @returns {Element[]} A new array of the same elements in sorted order.
   */
  static sortAccessoryItems(contentItems, { sortDirection = 'desc' } = {}) {
    const mappedItems = contentItems.map((item, index) => ({
      item,
      index,
      name: this._getAlphabeticalName(item),
      sortOrder: this._getNormalizedSortOrder(item),
    }));

    const hasSpecifiedSortOrder = mappedItems.some(({ sortOrder }) => sortOrder != null);

    if (!hasSpecifiedSortOrder) {
      return mappedItems.sort((a, b) => a.name.localeCompare(b.name)).map(({ item }) => item);
    }

    return mappedItems
      .sort((a, b) => {
        const hasSortA = a.sortOrder != null;
        const hasSortB = b.sortOrder != null;

        if (hasSortA && hasSortB && a.sortOrder !== b.sortOrder) {
          return sortDirection === 'asc' ? a.sortOrder - b.sortOrder : b.sortOrder - a.sortOrder;
        }

        if (hasSortA !== hasSortB) {
          return hasSortA ? -1 : 1;
        }

        const byName = a.name.localeCompare(b.name);
        if (byName !== 0) return byName;

        return a.index - b.index;
      })
      .map(({ item }) => item);
  }
}

export { Sorting };
